<template>
  <div>
    <page-header :title="$t('system.text_130', [$t('ai.mcp.title')])" />
    <page-body needMarginBottom>
      <p class="mcp-description">
        {{ $t('ai.mcp.description') }}
        <a href="#" class="mcp-link" @click.prevent="handleViewMCPInfo">{{ $t('ai.mcp.view_info') }}</a>
      </p>
      <h2 class="section-title mb-3 mt-5">{{ $t('ai.mcp.section_title') }}</h2>
      <a-tabs v-model="activeTab" class="mcp-tabs">
        <a-tab-pane :key="'builtin'" :tab="$t('ai.mcp.builtin_agent')">
          <div class="mcp-tab-content">
            <p class="mcp-tab-description">
              {{ $t('ai.mcp.builtin_description') }}
            </p>
            <a-form-model ref="form" :model="form" :rules="llmConfigRules" v-bind="formItemLayout">
              <a-form-model-item :label="$t('table.title.name')" prop="generate_name">
                <a-input v-model="form.generate_name" :placeholder="namePlaceholder" />
                <template #extra>
                  <name-repeated
                    res="mcp_agents"
                    version="v1"
                    :name="form.generate_name" />
                </template>
              </a-form-model-item>
              <a-form-model-item :label="$t('ai.mcp.llm_backend')" prop="llm_backend_config">
                <div class="mcp-config-item">
                  <span class="mcp-value">{{ llmBackendDisplay }}</span>
                  <a-button type="link" class="ml-3" @click="handleConfigLLM">{{ $t('ai.mcp.config') }}</a-button>
                </div>
              </a-form-model-item>
            </a-form-model>
          </div>
        </a-tab-pane>

            <a-tab-pane :key="'cursor'" :tab="$t('ai.mcp.cursor')">
              <div class="mcp-tab-content">
                <p class="mcp-tab-description">
                  {{ $t('ai.mcp.cursor_description') }}
                </p>
                <div class="mcp-code-wrapper">
                  <pre class="mcp-code-block"><code>{{ cursorConfigJson }}</code></pre>
                  <a-icon type="copy" class="mcp-copy-icon" @click="handleCopy(cursorConfigJson)" />
                </div>
              </div>
            </a-tab-pane>

            <a-tab-pane :key="'cloudcode'" :tab="$t('ai.mcp.cloud_code')">
              <div class="mcp-tab-content">
                <p class="mcp-tab-description">
                  {{ $t('ai.mcp.cloud_code_description') }}
                </p>
                <div class="mcp-code-wrapper">
                  <pre class="mcp-code-block"><code>{{ cloudCodeCommand }}</code></pre>
                  <a-icon type="copy" class="mcp-copy-icon" @click="handleCopy(cloudCodeCommand)" />
                </div>
              </div>
            </a-tab-pane>
      </a-tabs>

    </page-body>

    <page-footer>
      <div slot="right">
        <a-button type="primary" :disabled="activeTab !== 'builtin'" :loading="loading" @click="handleConfirm">{{ $t('common.create') }}</a-button>
        <a-button class="ml-3" @click="handleCancel">{{ $t('common.cancel') }}</a-button>
      </div>
    </page-footer>
  </div>
</template>

<script>
import { validateModelForm } from '@/utils/validate'
import WindowsMixin from '@/mixins/windows'
import NameRepeated from '@/sections/NameRepeated'

export default {
  name: 'McpCreate',
  components: {
    NameRepeated,
  },
  mixins: [WindowsMixin],
  data () {
    return {
      loading: false,
      activeTab: 'builtin',
      form: {
        generate_name: '',
        llm_backend_config: {},
      },
      llmConfigRules: {
        generate_name: [
          { required: true, message: this.$t('common.tips.input', [this.$t('table.title.name')]), trigger: 'blur' },
        ],
        llm_backend_config: {
          validator: (rule, value, callback) => {
            if (this.activeTab === 'builtin') {
              if (!value || !value.aiproxy_routing_id) {
                callback(new Error(this.$t('common.tips.select', [this.$t('ai.mcp.llm_backend')])))
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
        },
      },
      formItemLayout: {
        wrapperCol: {
          span: 20,
        },
        labelCol: {
          span: 4,
        },
      },
      cursorConfigJson: `{
        "mcpServers": {
          "server-name": {
            "url": "http://localhost:3000/mcp",
            "headers": {
              "API_KEY": "value"
            }
          }
        }
      }`,
      cloudCodeCommand: 'claude mcp add --transport sse private-api https://api.company.com/sse --header "X-API-Key: your-key-here"',
    }
  },
  computed: {
    llmBackendDisplay () {
      const config = this.form.llm_backend_config
      if (config && config.label) {
        return config.label
      }
      if (config && config.aiproxy_routing_id) {
        if (config.model) {
          return `${this.$t('aice.aiproxy.routing')} - ${config.model}`
        }
        return this.$t('aice.aiproxy.routing')
      }
      return this.$t('ai.mcp.not_configured')
    },
    namePlaceholder () {
      return this.$t('common.placeholder')
    },
  },
  created () {
    this.manager = new this.$Manager('mcp_agents', 'v1')
  },
  methods: {
    handleViewMCPInfo () {
      this.$message.info(this.$t('ai.mcp.view_info'))
    },
    handleConfigLLM () {
      this.createDialog('McpConfigDialog', {
        initialConfig: this.form.llm_backend_config,
        onOk: (config) => {
          this.form.llm_backend_config = config
        },
      })
    },
    genParams () {
      const config = this.form.llm_backend_config || {}
      const params = {
        name: this.form.generate_name,
        description: this.form.description,
        llm_driver: config.llm_driver || 'openai',
        llm_url: config.llm_url,
        model: config.model,
        aiproxy_routing_id: config.aiproxy_routing_id,
        aiproxy_virtual_key_id: config.aiproxy_virtual_key_id,
      }
      return params
    },
    async handleConfirm () {
      this.loading = true
      try {
        if (this.activeTab === 'builtin') {
          await validateModelForm(this.$refs.form)
        }
        const data = this.genParams()
        await this.manager.create({
          data,
        })
        this.$message.success(this.$t('common.success'))
        this.$router.push('/mcp')
      } catch (error) {
        throw error
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      this.$router.push('/mcp')
    },
    handleCopy (text) {
      if (!text) return

      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.$message.success(this.$t('common.text00095'))
      } catch (err) {
        this.$message.error(this.$t('common.text00096'))
      } finally {
        document.body.removeChild(textarea)
      }
    },
  },
}
</script>

<style lang="less" scoped>
.mcp-create-page {
  .mcp-header {
    margin-bottom: 32px;
    padding: 0 24px;
  }

  .mcp-description {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 16px;
  }

  .mcp-title {
    font-size: 24px;
    font-weight: bold;
    color: #000;
    margin-bottom: 0;
  }

  .mcp-link {
    color: #1890ff;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .mcp-tabs {
    ::v-deep .ant-tabs-tab {
      font-size: 14px;
    }
  }

  .mcp-tab-content {
    padding: 24px 0;
  }

  .mcp-tab-description {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .mcp-config-item {
    display: flex;
    align-items: center;
    font-size: 14px;
  }

  .mcp-label {
    margin-right: 8px;
    color: #666;
  }

  .mcp-value {
    color: #000;
    margin-right: 16px;
  }

}

.mcp-code-wrapper {
  position: relative;

  &:hover .mcp-copy-icon {
    opacity: 1;
    border-radius: 4px;
  }
}

.mcp-code-block {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px;
  overflow-x: auto;
  background-color: #f0f0f0;

  code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    font-size: 13px;
    color: #333;
    white-space: pre;
  }
}

.mcp-copy-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, color 0.2s;
  z-index: 10;
  background: rgba(255, 255, 255, 0.8);
  padding: 4px;
  border-radius: 2px;

  &:hover {
    color: #1890ff;
    background: rgba(255, 255, 255, 0.95);
  }
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  color: #000;
}
</style>
